import { QueryClient, useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  CreateRadiologistNoteError,
  CreateRadiologistNoteRequest,
  CreateRadiologistNoteResponse,
  GetRadiologistNoteError,
  GetRadiologistNoteRequest,
  GetRadiologistNoteResponse,
  PatchRadiologistNoteError,
  PatchRadiologistNoteRequest,
  PatchRadiologistNoteResponse,
} from '@/api/model';

const KEY = {
  GET_RADIOLOGIST_NOTE: 'GET_RADIOLOGIST_NOTE',
} as const;

const request = {
  createRadiologistNote: async ({ payload }: CreateRadiologistNoteRequest) => {
    const { data } = await fetchClient.FCT.post<CreateRadiologistNoteResponse>(
      `${ENDPOINTS.FCT.RADIOLOGIST_NOTE.REPORT}${payload.reportUuid}`,
      { body: payload.body },
    );

    return data;
  },
  getRadiologistNote: async (
    { payload: { reportUuid } }: GetRadiologistNoteRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<GetRadiologistNoteResponse>(
      `${ENDPOINTS.FCT.RADIOLOGIST_NOTE.REPORT}${reportUuid}`,
      { signal },
    );

    return data;
  },
  patchRadiologistNote: async ({ payload }: PatchRadiologistNoteRequest) => {
    const { data } = await fetchClient.FCT.patch<PatchRadiologistNoteResponse>(
      `${ENDPOINTS.FCT.RADIOLOGIST_NOTE.REPORT}${payload.reportUuid}`,
      { body: payload.body },
    );

    return data;
  },
};

const query = {
  useGetRadiologistNote: (
    { payload }: GetRadiologistNoteRequest,
    config?: Partial<UseQueryOptions<GetRadiologistNoteResponse, GetRadiologistNoteError>>,
  ) =>
    useQuery<GetRadiologistNoteResponse, GetRadiologistNoteError>({
      enabled: !!payload.reportUuid,
      queryFn: ({ signal }) => request.getRadiologistNote({ payload }, signal),
      queryKey: [KEY.GET_RADIOLOGIST_NOTE, payload.reportUuid],
      staleTime: Infinity,
      ...config,
    }),
};

const mutation = {
  useCreateRadiologistNote: (queryClient: QueryClient) =>
    useMutation<
      CreateRadiologistNoteResponse,
      CreateRadiologistNoteError,
      CreateRadiologistNoteRequest
    >({
      mutationFn: request.createRadiologistNote,
      onSuccess: (createdNote, { payload }) => {
        queryClient.setQueryData<GetRadiologistNoteResponse>(
          [KEY.GET_RADIOLOGIST_NOTE, payload.reportUuid],
          (old) => ({
            draft: createdNote,
            published: old?.published ?? [],
          }),
        );
      },
    }),
  usePatchRadiologistNote: (queryClient: QueryClient) =>
    useMutation<
      PatchRadiologistNoteResponse,
      PatchRadiologistNoteError,
      PatchRadiologistNoteRequest
    >({
      mutationFn: request.patchRadiologistNote,
      onSuccess: (patchedNote, { payload }) => {
        queryClient.setQueryData<GetRadiologistNoteResponse>(
          [KEY.GET_RADIOLOGIST_NOTE, payload.reportUuid],
          (old) => ({
            draft: patchedNote,
            published: old?.published ?? [],
          }),
        );
      },
    }),
};

export const radiologistNote = {
  KEY,
  mutation,
  query,
  request,
};
